/**
 * Unified Chef Reminder Modal
 * 
 * Displays Chef reminders for both photos and check-ins during live sessions.
 * Provides a unified interface where users can both take photos and provide voice/text check-ins.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Text, Heading } from './ui';

const UnifiedChefReminderModal = ({ 
    isOpen, 
    onClose, 
    reminder, 
    onPhotoTaken, 
    onCheckInResponse,
    onDismiss,
    voiceService,     // Pass voice service from LiveSession
    onSpeakText,      // Pass speak function from LiveSession
    chefReminders     // Pass chefReminders service for AI processing
}) => {
    const [response, setResponse] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isListeningForVoice, setIsListeningForVoice] = useState(false);
    const [checkInState, setCheckInState] = useState('photo'); // 'photo', 'checkin', 'complete'
    const [retryCount, setRetryCount] = useState(0);
    const [autoVoiceEnabled, setAutoVoiceEnabled] = useState(true);
    const [photoData, setPhotoData] = useState(null);
    const [hasPhoto, setHasPhoto] = useState(false);
    const [photoSkipped, setPhotoSkipped] = useState(false);
    
    const originalHandlerRef = useRef(null);
    const originalBypassTriggerRef = useRef(false);
    const noResponseTimerRef = useRef(null);
    const transcriptBufferRef = useRef('');
    const stateRef = useRef('photo');
    const lastProcessedLengthRef = useRef(0);
    const voiceTimeoutRef = useRef(null);

    // Reset state when reminder changes
    useEffect(() => {
        setResponse('');
        setIsSubmitting(false);
        setIsListeningForVoice(false);
        setCheckInState('photo');
        setRetryCount(0);
        setPhotoData(null);
        setHasPhoto(false);
        setPhotoSkipped(false);
        transcriptBufferRef.current = '';
        stateRef.current = 'photo';
        lastProcessedLengthRef.current = 0;
        
        // Clear any existing timers
        if (noResponseTimerRef.current) {
            clearTimeout(noResponseTimerRef.current);
            noResponseTimerRef.current = null;
        }
        if (voiceTimeoutRef.current) {
            clearTimeout(voiceTimeoutRef.current);
            voiceTimeoutRef.current = null;
        }
    }, [reminder]);

    // Auto-start photo prompt when modal opens
    useEffect(() => {
        if (isOpen && reminder?.type === 'unified' && onSpeakText && autoVoiceEnabled) {
            // Start with photo prompt
            onSpeakText("Would you like to take a photo of your progress? You can say 'skip photo' if you'd prefer not to.");
            
            // Wait for AI to finish speaking, then start listening
            setTimeout(() => {
                if (isOpen && stateRef.current === 'photo') {
                    startPhotoVoiceListening();
                }
            }, 3000); // 3 seconds to ensure AI finishes speaking
        }
    }, [isOpen, reminder?.type, autoVoiceEnabled]);

    // Cleanup voice service when modal closes
    useEffect(() => {
        if (!isOpen && isListeningForVoice) {
            stopVoiceInput();
        }
    }, [isOpen]);

    if (!reminder) return null;

    // Photo voice listening flow
    const startPhotoVoiceListening = () => {
        if (!voiceService) return;
        
        setIsListeningForVoice(true);
        
        // Store the original voice result handler
        if (!originalHandlerRef.current) {
            originalHandlerRef.current = voiceService.onResult;
        }
        if (originalBypassTriggerRef.current === undefined) {
            originalBypassTriggerRef.current = voiceService.bypassTrigger;
        }
        
        // Enable bypass trigger mode for continuous listening
        voiceService.setBypassTrigger(true);
        
        // Set up voice handler for photo phase
        voiceService.setOnResult((transcript, isTriggered) => {
            if (transcript && transcript.trim()) {
                const trimmedTranscript = transcript.trim().toLowerCase();
                
                // Check for skip commands
                if (trimmedTranscript.includes('skip') || trimmedTranscript.includes('no photo') || trimmedTranscript.includes('no thanks')) {
                    setPhotoSkipped(true);
                    stopVoiceInput(true);
                    moveToCheckInPhase();
                    return;
                }
                
                // Check for yes/photo commands
                if (trimmedTranscript.includes('yes') || trimmedTranscript.includes('photo') || trimmedTranscript.includes('sure')) {
                    stopVoiceInput(true);
                    handlePhotoCapture();
                    return;
                }
                
                // If unclear, ask again
                setTimeout(() => {
                    onSpeakText?.("I didn't catch that. Would you like to take a photo? Say 'yes' for photo or 'skip photo' to continue.");
                }, 1000);
            }
        });
        
        voiceService.startListening();
        
        // Set timeout for no response
        noResponseTimerRef.current = setTimeout(() => {
            handleNoPhotoResponse();
        }, 10000);
    };

    const handleNoPhotoResponse = () => {
        if (retryCount === 0) {
            setRetryCount(1);
            stopVoiceInput(true);
            
            setTimeout(() => {
                onSpeakText?.("Would you like to take a photo? Say 'yes' for photo or 'skip photo' to continue.");
                
                setTimeout(() => {
                    if (isOpen && stateRef.current === 'photo') {
                        startPhotoVoiceListening();
                    }
                }, 3000);
            }, 500);
        } else {
            // Skip photo after retry
            setPhotoSkipped(true);
            moveToCheckInPhase();
        }
    };

    const moveToCheckInPhase = () => {
        setCheckInState('checkin');
        stateRef.current = 'checkin';
        
        setTimeout(() => {
            onSpeakText?.(reminder.message || "How is this step going?");
            
            setTimeout(() => {
                if (isOpen && stateRef.current === 'checkin') {
                    startCheckInVoiceListening();
                }
            }, 3000);
        }, 500);
    };

    // Check-in voice listening flow
    const startCheckInVoiceListening = () => {
        if (!voiceService) return;
        
        setIsListeningForVoice(true);
        transcriptBufferRef.current = '';
        
        // Set up voice handler for check-in phase
        voiceService.setOnResult((transcript, isTriggered) => {
            if (transcript && transcript.trim()) {
                // Add to buffer
                const newText = transcript.trim();
                if (transcriptBufferRef.current) {
                    transcriptBufferRef.current += ' ' + newText;
                } else {
                    transcriptBufferRef.current = newText;
                }
                setResponse(transcriptBufferRef.current);
                
                // Clear timeout and set new one for complete response
                if (noResponseTimerRef.current) {
                    clearTimeout(noResponseTimerRef.current);
                }
                
                // Wait for user to finish speaking (2 seconds of silence)
                noResponseTimerRef.current = setTimeout(() => {
                    processCompleteCheckInResponse();
                }, 2000);
            }
        });
        
        voiceService.startListening();
        
        // Set initial timeout for no response
        noResponseTimerRef.current = setTimeout(() => {
            handleNoCheckInResponse();
        }, 15000);
    };

    const processCompleteCheckInResponse = async () => {
        if (!transcriptBufferRef.current.trim()) return;
        
        stopVoiceInput(true);
        
        // Process through AI for formatting
        if (chefReminders) {
            try {
                const formattedText = await chefReminders.processVoiceInput(transcriptBufferRef.current);
                setResponse(formattedText);
                transcriptBufferRef.current = formattedText;
            } catch (error) {
                console.error('Error processing voice input:', error);
                // Keep original text if AI processing fails
            }
        }
        
        // Check for stop intent
        if (chefReminders) {
            try {
                const stopIntent = await chefReminders.detectStopIntent(transcriptBufferRef.current);
                if (stopIntent.shouldStop && stopIntent.confidence > 0.7) {
                    submitUnifiedResponse(transcriptBufferRef.current);
                    return;
                }
            } catch (error) {
                console.error('Error detecting stop intent:', error);
            }
        }
        
        // Ask for follow-up
        setTimeout(() => {
            onSpeakText?.('Anything else to add?');
            
            setTimeout(() => {
                if (isOpen && stateRef.current === 'checkin') {
                    startFollowUpListening();
                }
            }, 3000);
        }, 500);
    };

    const startFollowUpListening = () => {
        if (!voiceService) return;
        
        setIsListeningForVoice(true);
        lastProcessedLengthRef.current = transcriptBufferRef.current.length;
        
        voiceService.setOnResult((transcript, isTriggered) => {
            if (transcript && transcript.trim()) {
                const newText = transcript.trim();
                transcriptBufferRef.current += ' ' + newText;
                setResponse(transcriptBufferRef.current);
                
                // Clear timeout and set new one
                if (noResponseTimerRef.current) {
                    clearTimeout(noResponseTimerRef.current);
                }
                
                noResponseTimerRef.current = setTimeout(() => {
                    processFollowUpResponse();
                }, 2000);
            }
        });
        
        voiceService.startListening();
        
        noResponseTimerRef.current = setTimeout(() => {
            submitUnifiedResponse(transcriptBufferRef.current);
        }, 7000);
    };

    const processFollowUpResponse = async () => {
        stopVoiceInput(true);
        
        // Extract only the NEW input since last processing
        const newInput = transcriptBufferRef.current.substring(lastProcessedLengthRef.current).trim();
        
        // Check if user said "no" or similar
        const lowerNewInput = newInput.toLowerCase();
        if (!newInput || 
            lowerNewInput === 'no' || 
            lowerNewInput === 'nope' || 
            lowerNewInput === "that's it" || 
            lowerNewInput === "that's all" ||
            lowerNewInput === "nothing else" ||
            lowerNewInput === "nothing") {
            
            const finalText = transcriptBufferRef.current.substring(0, lastProcessedLengthRef.current).trim();
            submitUnifiedResponse(finalText);
        } else {
            // User provided more information
            lastProcessedLengthRef.current = transcriptBufferRef.current.length;
            
            // Process through AI
            if (chefReminders) {
                try {
                    const formattedText = await chefReminders.processVoiceInput(transcriptBufferRef.current);
                    setResponse(formattedText);
                    transcriptBufferRef.current = formattedText;
                } catch (error) {
                    console.error('Error processing follow-up:', error);
                }
            }
            
            setTimeout(() => {
                onSpeakText?.('Anything else?');
                
                setTimeout(() => {
                    if (isOpen && stateRef.current === 'checkin') {
                        startFollowUpListening();
                    }
                }, 3000);
            }, 500);
        }
    };

    const handleNoCheckInResponse = () => {
        if (retryCount === 0) {
            setRetryCount(1);
            stopVoiceInput(true);
            
            setTimeout(() => {
                onSpeakText?.(reminder.message || "How is this step going?");
                
                setTimeout(() => {
                    if (isOpen && stateRef.current === 'checkin') {
                        startCheckInVoiceListening();
                    }
                }, 3000);
            }, 500);
        } else {
            setCheckInState('complete');
            stateRef.current = 'complete';
            stopVoiceInput(false);
            
            onSpeakText?.('Skipping check-in');
            
            setTimeout(() => {
                onClose();
            }, 2000);
        }
    };

    const submitUnifiedResponse = async (finalResponse) => {
        if (!finalResponse || !reminder) return;
        
        setIsSubmitting(true);
        setCheckInState('complete');
        stateRef.current = 'complete';
        stopVoiceInput(false);
        
        try {
            // Submit both photo and check-in if available
            if (hasPhoto && photoData) {
                await onPhotoTaken?.(reminder, photoData);
            }
            
            if (finalResponse.trim()) {
                await onCheckInResponse?.(reminder, finalResponse.trim());
            }
            
            onClose();
        } catch (error) {
            console.error('Error submitting unified response:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const stopVoiceInput = (keepHandlers = false) => {
        if (!voiceService) return;
        
        setIsListeningForVoice(false);
        
        if (noResponseTimerRef.current) {
            clearTimeout(noResponseTimerRef.current);
            noResponseTimerRef.current = null;
        }
        
        if (!keepHandlers) {
            if (originalHandlerRef.current) {
                voiceService.setOnResult(originalHandlerRef.current);
                originalHandlerRef.current = null;
            }
            
            if (originalBypassTriggerRef.current !== undefined) {
                voiceService.setBypassTrigger(originalBypassTriggerRef.current);
                originalBypassTriggerRef.current = undefined;
            }
        }
        
        voiceService.stopListening();
    };

    const handlePhotoCapture = async () => {
        try {
            // Import photo capture service
            const photoCapture = (await import('../services/photoCapture')).default;
            
            const blob = await photoCapture.capturePhotoWithUI();
            const compressedBlob = await photoCapture.compressImage(blob, 1024, 0.8);
            const photoData = photoCapture.createPhotoData(compressedBlob, reminder);
            
            setPhotoData(photoData);
            setHasPhoto(true);
        } catch (error) {
            console.error('Error capturing photo:', error);
        }
    };

    const handleSubmit = async () => {
        if (!response.trim() && !hasPhoto) {
            return;
        }

        setIsSubmitting(true);
        
        try {
            await submitUnifiedResponse(response);
        } catch (error) {
            console.error('Error handling reminder response:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDismiss = () => {
        onDismiss?.(reminder);
        onClose();
    };

    const getReminderIcon = () => {
        let imageSrc;
        if (isListeningForVoice) {
            imageSrc = '/images/Chef-listening.png';
        } else if (checkInState === 'complete' || checkInState === 'skipping') {
            imageSrc = '/images/Chef-talking.png';
        } else {
            imageSrc = '/images/Chef.png';
        }
        
        return (
            <img 
                src={imageSrc} 
                alt="Chef" 
                className="w-12 h-12 object-cover"
            />
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={handleDismiss} maxWidth="md">
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0">{getReminderIcon()}</div>
                    <div className="flex-1">
                        <Heading level={3} className="text-xl font-medium mb-1">
                            Quick Update
                        </Heading>
                        <Text className="text-sm opacity-60">
                            Step {reminder.stepIndex + 1}
                        </Text>
                    </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                    <Text className="text-base leading-relaxed">
                        {reminder.message}
                    </Text>
                </div>

                {/* Unified Content */}
                <div className="space-y-4">
                    {/* Auto Voice Toggle */}
                    <div className="flex items-center justify-between p-3 bg-neutral-50 rounded">
                        <Text className="text-sm font-medium">
                            Chef Auto-Ask
                        </Text>
                        <button
                            onClick={() => setAutoVoiceEnabled(!autoVoiceEnabled)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                                autoVoiceEnabled ? 'bg-neutral-900' : 'bg-neutral-300'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                                    autoVoiceEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Voice Status Indicator */}
                    {isListeningForVoice && (
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded flex items-center gap-2">
                            <div className="text-blue-600">🎤</div>
                            <Text className="text-sm text-blue-900">
                                {checkInState === 'photo' && 'Listening for photo decision...'}
                                {checkInState === 'checkin' && 'Listening for your update...'}
                            </Text>
                        </div>
                    )}
                    
                    {/* Photo Section */}
                    {checkInState === 'photo' && (
                        <div className="bg-neutral-50 p-4 rounded border">
                            <Text className="text-sm font-medium mb-2 block">
                                📸 Progress Photo
                            </Text>
                            <Text className="text-sm opacity-80 mb-3">
                                Would you like to capture a photo of your progress?
                            </Text>
                            
                            {hasPhoto ? (
                                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                                    <span className="text-green-600">✅</span>
                                    <Text className="text-sm text-green-900">Photo captured</Text>
                                    <button
                                        onClick={() => {
                                            setPhotoData(null);
                                            setHasPhoto(false);
                                        }}
                                        className="text-xs px-2 py-1 bg-red-100 text-red-900 rounded hover:bg-red-200"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handlePhotoCapture}
                                        disabled={isSubmitting}
                                        className="flex-1 px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 disabled:opacity-50"
                                    >
                                        📸 Take Photo
                                    </button>
                                    <button
                                        onClick={() => {
                                            setPhotoSkipped(true);
                                            moveToCheckInPhase();
                                        }}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 disabled:opacity-50"
                                    >
                                        Skip Photo
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Check-in Text Area */}
                    {checkInState === 'checkin' && (
                        <div>
                            <Text className="text-sm font-medium mb-2 block">
                                How's it going?
                            </Text>
                            <textarea
                                value={response}
                                onChange={(e) => setResponse(e.target.value)}
                                placeholder={isListeningForVoice ? "🎤 Listening..." : "Type or speak your response..."}
                                className="w-full p-3 border border-neutral-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent"
                                rows={3}
                                maxLength={500}
                                disabled={isListeningForVoice}
                            />
                            <div className="flex justify-between items-center mt-1">
                                <Text className="text-xs opacity-60">
                                    {response.length}/500 characters
                                </Text>
                                {voiceService && !isListeningForVoice && (
                                    <button
                                        onClick={startCheckInVoiceListening}
                                        className="text-xs px-2 py-1 bg-neutral-900 text-white rounded hover:opacity-60"
                                    >
                                        🎤 Use Voice
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Show photo if captured */}
                    {hasPhoto && checkInState === 'checkin' && (
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                            <div className="flex items-center gap-2">
                                <span className="text-green-600">📸</span>
                                <Text className="text-sm text-green-900">Photo captured and ready to include</Text>
                            </div>
                        </div>
                    )}
                    
                    {/* Submit/Skip Buttons */}
                    {checkInState === 'checkin' && (
                        <div className="flex gap-3">
                            <button
                                onClick={handleSubmit}
                                disabled={(!response.trim() && !hasPhoto) || isSubmitting}
                                className="flex-1 px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Saving...' : 'Send Update'}
                            </button>
                            <button
                                onClick={handleDismiss}
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 disabled:opacity-50"
                            >
                                Skip
                            </button>
                        </div>
                    )}
                    
                    {/* Show processing state */}
                    {checkInState === 'complete' && (
                        <div className="text-center py-2">
                            <Text className="text-sm opacity-60">
                                ✅ Saving your update...
                            </Text>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-neutral-200">
                    <Text className="text-xs opacity-60 text-center">
                        Chef reminders help capture your cooking journey
                    </Text>
                </div>
            </div>
        </Modal>
    );
};

export default UnifiedChefReminderModal;
